<script setup lang="ts">
  import { useCustomerStore } from '@/store/customer.store'
  import { useUserStore } from '@/store/user.store'
  import { computed, onMounted, ref, watch } from 'vue'
  import {
    FormFieldContainer,
    FormInput,
    UiButton,
    deepEqual,
    useToastStore,
    type Tab,
  } from '@logistiek-zonder-papier/lzp-library'
  import { useAddressStore } from '@/store/address.store'
  import { checkVAT, belgium, netherlands } from 'jsvat-next'
  import { type Address, type Customer } from '@/types/nest/customer'
  import CustomerAddressForm from '@/components/customer/CustomerAddressForm.vue'
  import CustomerAddAddress from '@/components/customer/CustomerAddAddress.vue'
  import { electronicFormatIBAN, validateIBAN } from 'ibantools'
  import { useCustomerValidation } from '@/composables/validation/use-customer-validation'
  import { useTabQuery } from '@/composables/use-tab-query'
  import DiwassCredentials from '@/components/customer/DiwassCredentials.vue'
  import PortalCustomization from '@/components/customer/PortalCustomization.vue'
  import Tabs from '@/components/Tabs.vue'
  import TopBar from '@/components/TopBar.vue'
  import { NestUserRole } from '@/types/user'
  import { hasJwtRole } from '@/utils/jwt.utils'

  const canCustomizePortal = hasJwtRole(NestUserRole.ADMIN)

  const tabs: Tab[] = [
    { key: 'general', label: 'General' },
    { key: 'billing', label: 'Billing' },
    { key: 'diwass', label: 'Diwass' },
    ...(canCustomizePortal ? [{ key: 'branding', label: 'Branding' }] : []),
  ]
  const activeTab = useTabQuery(tabs, 'general')

  const userStore = useUserStore()
  const customerStore = useCustomerStore()
  const addressStore = useAddressStore()
  const toastStore = useToastStore()

  const warning = ref<string | false>(false)
  const ibanWarning = ref<string | false>(false)

  const customer = ref<Customer | null>(null)
  const address = ref<Address | null>(null)
  const billingAddress = ref<Address | null>(null)

  const originalCustomer = ref<Customer | null>(null)
  const originalAddress = ref<Address | null>(null)
  const originalBillingAddress = ref<Address | null>(null)

  const hasCustomerChanges = ref(false)
  const hasBillingCustomerChanges = ref(false)
  const hasAddressChanges = ref(false)
  const hasBillingAddressChanges = ref(false)

  const showAddressModal = ref(false)

  const isAddAddress = ref(false)
  const addressBeingEdited = ref<Address | null>(null)
  const editingTarget = ref<'general' | 'billing' | null>(null)

  const hasGeneralChanges = computed<boolean>(
    () => hasCustomerChanges.value || hasAddressChanges.value
  )
  const hasBillingChanges = computed<boolean>(
    () => hasBillingCustomerChanges.value || hasBillingAddressChanges.value
  )

  const generalValidation = useCustomerValidation(customer, 'general')
  const billingValidation = useCustomerValidation(customer, 'billing')

  function openEditAddress(target: 'general' | 'billing') {
    isAddAddress.value = false
    editingTarget.value = target
    addressBeingEdited.value = target === 'general' ? address.value : billingAddress.value
    showAddressModal.value = true
  }

  function openAddAddress(target: 'general' | 'billing') {
    isAddAddress.value = true
    editingTarget.value = target
    addressBeingEdited.value = null
    showAddressModal.value = true
  }

  async function onAddressSaved(saved: Address) {
    if (editingTarget.value === 'general') {
      address.value = saved
      originalAddress.value = { ...saved }

      syncMirroredAddress(saved, 'billing')

      await addressStore.updateCustomerAddress({
        ...address.value,
        id: customerStore.customer?.addressId,
      })
    } else if (editingTarget.value === 'billing') {
      billingAddress.value = saved
      originalBillingAddress.value = { ...saved }

      syncMirroredAddress(saved, 'general')

      await addressStore.updateCustomerAddress({
        ...billingAddress.value,
        id: customerStore.customer?.billingAddressId,
      })
    }
  }

  onMounted(async () => {
    await userStore.fetchMe()
    await addressStore.fetchAddresses()

    const customerId = userStore.currentNestUser?.customerId
    if (!customerId) return

    await customerStore.fetchCustomer(customerId)
    const fetchedCustomer = customerStore.customer

    if (!fetchedCustomer) return
    customer.value = fetchedCustomer
    originalCustomer.value = { ...fetchedCustomer }

    if (fetchedCustomer.address) {
      address.value = fetchedCustomer.address
      originalAddress.value = { ...fetchedCustomer.address }
    }

    if (fetchedCustomer.billingAddress) {
      billingAddress.value = fetchedCustomer.billingAddress
      originalBillingAddress.value = { ...fetchedCustomer.billingAddress }
    }
  })

  async function checkVat() {
    const taxNumber = customerStore.customer?.taxNumber

    if (taxNumber == null) return

    warning.value =
      taxNumber.length > 0 && !checkVAT(taxNumber, [netherlands, belgium]).isValid
        ? 'Ongeldig ondernemingsnummer'
        : false

    if (!checkVAT(taxNumber, [netherlands, belgium]).isValid) {
      return
    }

    const response = await customerStore.checkCustomerVat(taxNumber)

    addressStore.address = {
      ...response.address,
      houseNumber: parseInt(response.address.number).toString(),
      postalCode: response.address.zip_code,
    }
  }

  function checkIban() {
    const iban = customer.value?.iban

    if (!iban || iban.length === 0) {
      ibanWarning.value = false
      return
    }

    const result = validateIBAN(electronicFormatIBAN(iban) ?? iban)
    ibanWarning.value = result.valid ? false : 'Ongeldig IBAN-nummer'
  }

  async function submitGeneral() {
    if (!generalValidation.validate()) {
      toastStore.error('Een of meerdere velden zijn niet goed ingevuld')
      return
    }

    let didSave = false

    if (customer.value && hasCustomerChanges.value) {
      await customerStore.updateCustomer(customer.value)
      originalCustomer.value = { ...customer.value }
      hasCustomerChanges.value = false
      didSave = true
    }

    if (address.value && hasAddressChanges.value) {
      await addressStore.updateCustomerAddress({
        ...address.value,
        id: customerStore.customer?.addressId,
      })
      originalAddress.value = { ...address.value }
      hasAddressChanges.value = false
      syncMirroredAddress(address.value, 'billing')
      didSave = true
    }

    if (didSave) toastStore.success('Customer data successfully updated')
  }

  async function submitBilling() {
    if (!billingValidation.validate()) {
      toastStore.error('Een of meerdere velden zijn niet goed ingevuld')
      return
    }

    let didSave = false

    if (customer.value && hasBillingCustomerChanges.value) {
      await customerStore.updateCustomer(customer.value)
      originalCustomer.value = { ...customer.value }
      hasBillingCustomerChanges.value = false
      didSave = true
    }

    if (billingAddress.value && hasBillingAddressChanges.value) {
      await addressStore.updateCustomerAddress({
        ...billingAddress.value,
        id: customerStore.customer?.billingAddressId,
      })
      originalBillingAddress.value = { ...billingAddress.value }
      hasBillingAddressChanges.value = false
      syncMirroredAddress(billingAddress.value, 'general')
      didSave = true
    }

    if (didSave) toastStore.success('Billing data successfully updated')
  }

  function syncMirroredAddress(saved: Address, mirror: 'general' | 'billing') {
    if (customerStore.customer?.addressId !== customerStore.customer?.billingAddressId) return
    if (mirror === 'billing') {
      billingAddress.value = { ...saved }
      originalBillingAddress.value = { ...saved }
      hasBillingAddressChanges.value = false
    } else {
      address.value = { ...saved }
      originalAddress.value = { ...saved }
      hasAddressChanges.value = false
    }
  }

  function normalizeEmpty<T extends Record<string, unknown>>(obj: T): T {
    const result = { ...obj }
    for (const key in result) {
      if (result[key] === '') {
        result[key] = null as T[Extract<keyof T, string>]
      }
    }
    return result
  }

  watch(
    () => customer.value?.taxNumber,
    () => checkVat()
  )

  watch(
    () => customer.value?.iban,
    () => checkIban()
  )

  watch(
    customer,
    current => {
      if (!current) {
        hasCustomerChanges.value = false
        hasBillingCustomerChanges.value = false
        return
      }
      if (!originalCustomer.value) {
        hasCustomerChanges.value = true
        hasBillingCustomerChanges.value = true
        return
      }

      const original = originalCustomer.value

      hasCustomerChanges.value = !deepEqual(
        normalizeEmpty({
          name: current.name,
          email: current.email,
          phone: current.phone,
          taxNumber: current.taxNumber,
        }),
        normalizeEmpty({
          name: original.name,
          email: original.email,
          phone: original.phone,
          taxNumber: original.taxNumber,
        })
      )

      hasBillingCustomerChanges.value = !deepEqual(
        normalizeEmpty({
          billingEmail: current.billingEmail,
          billingPhone: current.billingPhone,
          iban: current.iban,
          bic: current.bic,
        }),
        normalizeEmpty({
          billingEmail: original.billingEmail,
          billingPhone: original.billingPhone,
          iban: original.iban,
          bic: original.bic,
        })
      )
    },
    { deep: true }
  )
</script>

<template>
  <TopBar />
  <CustomerAddAddress
    v-model="showAddressModal"
    :existing-address="addressBeingEdited"
    :isEditable="true"
    @saved="onAddressSaved"
  />

  <div class="customer-page">
    <Tabs v-model="activeTab" :tabs="tabs">
      <template #general v-if="customer">
        <div class="customer">
          <FormFieldContainer>
            <FormInput
              label="Name"
              v-model="customer.name"
              :error="generalValidation.visibleErrors.value.name"
            />
            <FormInput
              label="Email"
              v-model="customer.email"
              :error="generalValidation.visibleErrors.value.email"
            />
            <FormInput label="Phone" v-model="customer.phone" />
            <FormInput
              label="Taxnumber"
              v-model="customer.taxNumber"
              placeholder="KBO / ondernemingsnummer / BTW-nummer / EORI-nummer"
              :error="warning ? warning : ''"
            />
          </FormFieldContainer>
        </div>
        <div v-if="address && originalAddress" class="address">
          <FormFieldContainer label="Address">
            <template #action-button>
              <UiButton icon="edit" @click="openEditAddress('general')" />
              <UiButton icon="plus" variant="prominent" @click="openAddAddress('general')" />
            </template>

            <CustomerAddressForm
              v-model="address"
              :saved="originalAddress"
              :isEditable="false"
              @hasChanged="hasAddressChanges = $event"
            />

            <UiButton
              label="Opslaan"
              class="save-button"
              :disabled="
                !hasGeneralChanges || !!generalValidation.isSubmittedAndInvalid.value || !!warning
              "
              variant="prominent"
              @click="submitGeneral"
            />
          </FormFieldContainer>
        </div>
      </template>

      <template #billing v-if="customer">
        <div class="customer">
          <FormFieldContainer>
            <FormInput
              label="Email"
              v-model="customer.billingEmail"
              :error="billingValidation.visibleErrors.value.billingEmail"
            />
            <FormInput label="Phone" v-model="customer.billingPhone" />
            <FormInput
              label="IBAN"
              v-model="customer.iban"
              placeholder="IBAN-nummer"
              :error="ibanWarning ? ibanWarning : ''"
              :max-length="34"
            />
            <FormInput label="BIC" v-model="customer.bic" />
          </FormFieldContainer>
        </div>

        <div v-if="billingAddress && originalBillingAddress" class="address">
          <FormFieldContainer label="Address">
            <template #action-button>
              <UiButton icon="edit" @click="openEditAddress('billing')" />
              <UiButton icon="plus" variant="prominent" @click="openAddAddress('billing')" />
            </template>

            <CustomerAddressForm
              v-model="billingAddress"
              :saved="originalBillingAddress"
              :isEditable="false"
              @hasChanged="hasBillingAddressChanges = $event"
            />

            <UiButton
              label="Opslaan"
              class="save-button"
              :disabled="
                !hasBillingChanges ||
                !!billingValidation.isSubmittedAndInvalid.value ||
                !!ibanWarning
              "
              variant="prominent"
              @click="submitBilling"
            />
          </FormFieldContainer>
        </div>
      </template>
      <template #diwass v-if="customer">
        <DiwassCredentials :customer="customer" />
      </template>
      <template #branding v-if="customer && canCustomizePortal">
        <PortalCustomization :customer="customer" />
      </template>
    </Tabs>
  </div>
</template>

<style scoped lang="css">
  .save-button {
    align-self: end;
  }

  .customer-page {
    display: flex;
    flex-direction: column;
  }

  .customer-container {
    padding: var(--spacing-m);
  }

  .data-container {
    display: flex;
    flex-direction: row;
    justify-content: space-around;
    flex-grow: 1;
    align-items: center;
    gap: var(--spacing-m);
    padding-bottom: var(--spacing-s);
  }

  .customer {
    width: 100%;
  }

  .address {
    width: 100%;
  }
</style>
